import { useEffect, useMemo, useRef, useState } from "react";
import { usePosicionDesplegable } from "../posicionDesplegable";
import Icon from "../Icon";
import { ST_CLR } from "../../constants";

// Enlaza una entrada del cuaderno con un PASO del gestor de proyectos nativo.
//
// Es el hermano de NotionSelector, y los dos conviven porque hay dos modelos:
// aquel escribe external_task_id / external_project_id (Notion) y este escribe
// task_id / project_id. Cada entrada puede estar enlazada a uno, al otro, o a
// ninguno; el endpoint de tiempo real del proyecto mira los dos.
//
// Esto es lo que cierra el circulo: las horas que ya se imputan aqui son las que
// la cronologia y el dashboard leen como tiempo real, sin pedirle a nadie que
// las reporte dos veces.
// `bloquearNoDisponibles` marca los pasos que todavia no se pueden EMPEZAR
// porque un predecesor del flujo no esta terminado (el servidor los devuelve con
// `bloqueado` y `bloqueada_por` cuando se le pide ?desbloqueo=1).
//
// Por defecto va apagado: el cuaderno imputa horas a POSTERIORI, tambien a pasos
// ya cerrados, y ahi cualquier paso vale. Lo enciende el cronometro, que si
// arranca trabajo nuevo.
export default function SelectorPaso({ taskId, pasos = [], onChange,
                                       bloquearNoDisponibles = false }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [aviso, setAviso] = useState(null);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(abierto, ref, panelRef, { altoMax: 280 });

  useEffect(() => {
    if (!abierto) return;
    const fuera = e => {
      if (ref.current && !ref.current.contains(e.target)) { setAbierto(false); setQ(""); }
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const enlazado = pasos.find(p => String(p.id) === String(taskId));

  // Se buscan las dos cosas a la vez, el paso y su proyecto: la gente recuerda
  // "algo de la migracion" antes que el titulo exacto del paso.
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = t
      ? pasos.filter(p =>
          String(p.title || "").toLowerCase().includes(t) ||
          String(p.project_name || "").toLowerCase().includes(t))
      : pasos;
    return base.slice(0, 14);
  }, [q, pasos]);

  const elegir = p => {
    onChange(String(p.id), String(p.project_id));
    setAbierto(false); setQ("");
  };

  // Un enlace a un paso que ya no existe (borrado desde el proyecto) no se
  // esconde: se avisa, porque las horas siguen contando en el proyecto.
  if (taskId && !enlazado) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
        borderRadius: 8, border: "1px solid var(--warn-line)", background: "var(--warn-bg)",
        width: "fit-content", maxWidth: "100%" }}>
        <Icon name="exclamation-triangle-fill" size={11} color="var(--warn-text)" />
        <span style={{ fontSize: 11.5, color: "var(--warn-text)", flex: 1 }}>
          El paso enlazado ya no esta
        </span>
        <button onMouseDown={e => { e.stopPropagation(); onChange(null, null); }}
          title="Quitar el enlace"
          style={{ background: "transparent", border: "none", cursor: "pointer",
            color: "var(--warn-text)", fontSize: 14, lineHeight: 1, padding: "0 0 0 4px" }}>×</button>
      </div>
    );
  }

  if (enlazado) {
    const clr = ST_CLR[enlazado.status] || "#6b7280";
    return (
      <div title={`${enlazado.project_name} · ${enlazado.status}`}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
          borderRadius: 8, border: "1px solid #c7d2fe", background: "var(--info-bg)",
          width: "fit-content", maxWidth: "100%" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: clr,
          flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--info-text)", flex: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {enlazado.title}
        </span>
        <span style={{ fontSize: 9.5, color: "#6366f1", flexShrink: 0, maxWidth: 110,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {enlazado.project_name}
        </span>
        <button onMouseDown={e => { e.stopPropagation(); onChange(null, null); }}
          title="Quitar el enlace"
          style={{ background: "transparent", border: "none", cursor: "pointer",
            color: "#a5b4fc", fontSize: 14, lineHeight: 1, padding: "0 0 0 4px" }}>×</button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <Icon name="diagram-3" size={11} color="var(--text3)"
          style={{ position: "absolute", left: 9, top: "50%",
            transform: "translateY(-50%)", pointerEvents: "none" }} />
        <input aria-label="Buscar paso de proyecto" value={q}
          onChange={e => { setQ(e.target.value); setAbierto(true); }}
          onFocus={() => setAbierto(true)}
          onKeyDown={e => {
            if (e.key === "Enter" && filtrados.length) { e.preventDefault(); elegir(filtrados[0]); }
            if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); setQ(""); }
          }}
          placeholder={pasos.length ? "Buscar paso del proyecto…" : "Sin pasos asignados"}
          disabled={!pasos.length}
          style={{ width: "100%", padding: "5px 8px 5px 26px", borderRadius: 8,
            border: "1px solid var(--border-ctl)", background: "var(--surface)",
            color: "var(--text)", fontSize: 12, fontFamily: "var(--font)" }} />
      </div>

      {aviso && (
        <div style={{ marginTop: 5, padding: "5px 9px", borderRadius: 8,
          border: "1px solid var(--warn-line)", background: "var(--warn-bg)", fontSize: 11,
          color: "var(--warn-text)", display: "flex", alignItems: "flex-start", gap: 5,
          lineHeight: 1.45 }}>
          <Icon name="sign-stop-fill" size={11} color="var(--warn-text)"
            style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            «{aviso.title}» espera a{" "}
            {(aviso.bloqueada_por || []).map(x => `«${x.title}»`).join(" y ")}
            . Se desbloquea en cuanto se marque como terminada.
          </span>
          <button onMouseDown={e => { e.stopPropagation(); setAviso(null); }}
            style={{ background: "transparent", border: "none", cursor: "pointer",
              color: "var(--warn-text)", fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
        </div>
      )}

      {abierto && !!pasos.length && (
        <div ref={panelRef} style={{ zIndex: 200,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--shadow-lg)", width: "min(440px, calc(100vw - 32px))",
          ...posicion }}>
          {filtrados.map((p, i) => {
            // Separador entre "los mios" y el resto: el orden lo decide el
            // servidor, aqui solo se hace visible el corte.
            const primerNoMio = !p.mia && i > 0 && filtrados[i - 1].mia;
            const bloq = bloquearNoDisponibles && p.bloqueado;
            return (
              <div key={p.id}>
                {primerNoMio && (
                  <div style={{ padding: "5px 12px 3px", fontSize: 9, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: ".07em",
                    color: "var(--text3)", borderTop: "1px solid var(--border2)" }}>
                    Otros pasos que puedes ver
                  </div>
                )}
                <div onMouseDown={() => (bloq ? setAviso(p) : elegir(p))}
                  title={bloq
                    ? `Espera a: ${(p.bloqueada_por || []).map(x => x.title).join(", ")}`
                    : `${p.project_name} · ${p.status}`}
                  style={{ padding: "7px 12px", display: "flex", opacity: bloq ? .55 : 1,
                    cursor: bloq ? "not-allowed" : "pointer",
                    alignItems: "center", gap: 8, fontSize: 12 }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--bg)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                  {bloq
                    ? <Icon name="sign-stop-fill" size={9} color="var(--warn-text)" />
                    : <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                        background: ST_CLR[p.status] || "#6b7280" }} />}
                  <span style={{ flex: 1, fontWeight: 500, overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title}</span>
                  <span style={{ fontSize: 10, color: "var(--text3)", flexShrink: 0,
                    maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap" }}>{p.project_name}</span>
                  {p.due_date && (
                    <span style={{ fontSize: 9.5, color: "var(--text3)", fontFamily: "var(--mono)",
                      flexShrink: 0 }}>{p.due_date.slice(5)}</span>
                  )}
                </div>
              </div>
            );
          })}
          {!filtrados.length && (
            <div style={{ padding: 12, fontSize: 12, color: "var(--text3)", textAlign: "center" }}>
              Sin resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
}
